import { useCallback, useEffect, useState, Children } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { lpcoEnvs, lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn"; // 시스템 공통 유틸
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table, Tree } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    useTree,
    usePage,
} from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_LPCO_INFO_U,
    SG_ORG_DEPT_USR_LIST_U,
    SG_SETT_OFCR_LIST_U,
} from "./services/OsciCfgnChpnService";
import { CommonErrors } from "@/comn/components";
import { size } from "lodash";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */
/*
 * Register Responsibility
 * !== Responsibility 등록 ==!
 */
export const OSCI0203025S = () => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { appFmId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    // useTree
    const uTree = useTree({
        _checkbox: false,
        _checked: [],
    });

    const [orgDeptTree, setOrgDeptTree] = useState([]);

    const [orgDeptUsrList, setOrgDeptUsrList] = useState(comnUtils.getGridData([]));
    const [chpnList, setChpnList] = useState(comnUtils.getGridData([]));

    // ORG LIST
    const [orgCdList, setOrgCdList] = useState([]);
    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0119" },
            { area: "comnCd", comnCd: "OSC0113" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Register LPCO Component !== LPCO Component 등록 ==!
        lpcoInfoRgsr: useForm({
            defaultSchema: SF_LPCO_INFO_U,
            defaultValues: {
                orgCd: auth.get("orgCd"),
                orgNm: auth.get("orgNm"),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Component List !== Component 목록 ==!
        orgDeptUsrList: useGrid({
            defaultSchema: SG_ORG_DEPT_USR_LIST_U,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        chpnList: useGrid({
            defaultSchema: SG_SETT_OFCR_LIST_U,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_orgDeptUsrList: {
            head: {},
            cell: {
                usrId: (props: any) => {
                    const { binding, rowValues, value } = props;
                    const usrId =
                        rowValues.usrId +
                        (rowValues.asisUsrId && rowValues.asisUsrId != rowValues.usrId
                            ? "(" + rowValues.asisUsrId + ")"
                            : "");
                    return (
                        <>
                            <FormControl type="text" edit={false} value={usrId} />
                        </>
                    );
                },
            },
            edit: {
                asnmtOfcrTpCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return (
                        <>
                            <FormControl
                                type="select"
                                value={rowValues.asnmtOfcrTpCd}
                                options={[...(rowValues.ofcrTpCds || [])]}
                                required={true}
                                onChange={(v) => {
                                    grid.orgDeptUsrList.updateRow({ ...rowValues, asnmtOfcrTpCd: v });
                                }}
                            />
                        </>
                    );
                },
                usrId: (props: any) => {
                    const { binding, rowValues, value } = props;
                    const usrId =
                        rowValues.usrId +
                        (rowValues.asisUsrId && rowValues.asisUsrId != rowValues.usrId
                            ? "(" + rowValues.asisUsrId + ")"
                            : "");
                    return (
                        <>
                            <FormControl type="text" edit={false} value={usrId} />
                        </>
                    );
                },
            },
        },

        grid_chpnList: {
            head: {},
            cell: {},
            edit: {},
        },
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        /* Register LPCO Component !== LPCO Component 등록 ==! */
        saveAppChpn: useFetch({
            api: (data) => APIS.saveAppChpn({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        navigate(URLS.osci0203028s + "/" + data.appSaveInfo.content.appFmId);
                    },
                    onCancel: () => {
                        navigate(URLS.osci0203028s + "/" + data.appSaveInfo.content.appFmId);
                    },
                });
            },
            onError: (error) => {
                modal.openModal({ content: error.response.data.errorMessage });
            },
        }),
        getOrgLpcoByPrcsList: useFetch({
            api: (data) => {
                return lpcoUtils.getOrgLpcoByPrcsList(data);
            },
            onSuccess: (data) => {
                setOrgLpcoList(data || []);
            },
            onError: () => {},
            showToast: false,
        }),
        getOrgDeptTreeData: useFetch({
            api: (data) => {
                return lpcoUtils.getOrgDeptTreeData(data);
            },
            onSuccess: (data) => {
                setOrgDeptTree(data);
                uTree.setData(data);
            },
            onError: () => {},
            showToast: false,
        }),
        getAppDtl: useFetch({
            api: (appCdNo) => {
                return APIS.getAppDtlByAppCdNo(appCdNo);
            },
            onSuccess: (data) => {
                form.lpcoInfoRgsr.setValues(data.appInfo.content?.info);
                let chpnList = data.appInfo.content?.chpnList || [];
                setChpnList(comnUtils.getGridData(chpnList));
            },
            onError: () => {},
            showToast: false,
        }),
        getOrgDeptUserList: useFetch({
            api: (orgCd, deptCd) => {
                return APIS.getOrgDeptUserList(orgCd, deptCd);
            },
            onSuccess: (data) => {
                const userList = data?.usrList?.content || [];
                setOrgDeptUsrList(comnUtils.getGridData(userList));
            },
            onError: () => {},
            showToast: false,
        }),
    };
    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : 재포장 품목 신청서 그리드 클릭
     *      ...
     * }
     */
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Save Component !== LPCO Component 저장 ==!
        saveAppChpn: form.lpcoInfoRgsr.handleSubmit(() => {
            // check duplicate
            const chpnList = grid.chpnList.getData();

            if (!chpnList || chpnList.length == 0) {
                // You can enter one or more DATA and save it.
                modal.openModal({
                    content: "msg.osc.00037",
                });
            } else {
                // Duplicate Check
                const orgDeptUserViewList = grid.orgDeptUsrList.getViewData();
                let errResult: { errors: any[] } = { errors: [] };
                const duplMap: { [key: string]: boolean } = {};
                let idx = 1;
                for (const item of orgDeptUserViewList) {
                    const usrId = item.usrId;
                    const chkVal = item.usrId + "##" + item.asnmtOfcrTpCd;
                    if (usrId && duplMap[chkVal]) {
                        errResult.errors.push(lpcoUtils.getErrorObj(idx, "usrId", "msg.osc.00017", usrId));
                    } else {
                        duplMap[chkVal] = true;
                    }
                    idx++;
                }
                if (errResult && errResult.errors?.length > 0) {
                    // sort by row
                    errResult.errors = lpcoUtils.errorListSortByRow(errResult.errors);
                    // error layer popup
                    modal.openModal({
                        content: <CommonErrors {...errResult} />,
                        size: "lg",
                        draggable: true,
                    });
                } else {
                    const data = { ...form.lpcoInfoRgsr.getValues(), chpnList: grid.chpnList.getData() };
                    modal.openModal({
                        content: "msg.osc.00090",
                        onConfirm: () => {
                            fetch.saveAppChpn.fetch(data);
                        },
                    });
                }
            }
        }),
        click_Btn_AddOfficer: async () => {
            const bValid = await form.lpcoInfoRgsr.validate();
            if (bValid) {
                const checkList = grid.orgDeptUsrList.getChecked() || []; // OGA의 부서 사용자 정보
                const chpnList = grid.chpnList.getViewData(); // LPCO에 연결된 담당자 정보

                if (checkList.length == 0) {
                    modal.openModal({ content: "msg.osc.00114" });
                } else {
                    // Role required
                    let errResult: { errors: any[] } = { errors: [] };
                    let idx = 1;
                    for (const item of checkList) {
                        const asnmtOfcrTpCd = item.asnmtOfcrTpCd;

                        if (!asnmtOfcrTpCd) {
                            errResult.errors.push(lpcoUtils.getErrorObj(idx, "L_ROLE_NM", "msg.osc.00013", ""));
                        } else {
                            // duplication check
                            const usrId = item.usrId;
                            const asisUsrId = item.asisUsrId;
                            const asnmtOfcrTpCd = item.asnmtOfcrTpCd;
                            for (const ofcrInfo of chpnList) {
                                const ofcrUsrId = ofcrInfo.usrId;
                                const ofcrTpCd = ofcrInfo.asnmtOfcrTpCd;
                                if (asnmtOfcrTpCd == ofcrTpCd && (usrId == ofcrUsrId || asisUsrId == ofcrUsrId)) {
                                    errResult.errors.push(
                                        lpcoUtils.getErrorObj(idx, "L_USR_ID", "msg.osc.00017", ofcrUsrId),
                                    );
                                }
                            }
                        }
                        idx++;
                    }

                    if (errResult.errors?.length > 0) {
                        // sort by row
                        errResult.errors = lpcoUtils.errorListSortByRow(errResult.errors);
                        // error layer popup
                        modal.openModal({
                            content: <CommonErrors {...errResult} />,
                            size: "lg",
                            draggable: true,
                        });
                    } else {
                        for (const item of checkList) {
                            const copyItem = { ...item };
                            delete copyItem.ofcrTpCds;
                            grid.chpnList.addRow(copyItem);
                        }
                    }
                }
            }
        },
        click_Btn_AddGridList: () => {
            grid.orgDeptUsrList.addRow();
        },
        click_Btn_DeleteGridList: () => {
            const seltList: any[] = grid.orgDeptUsrList.getChecked() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }

            grid.orgDeptUsrList.deleteRow("checkbox");

            // modal.openModal({
            //     content: "msg.osc.00095",
            //     onConfirm: () => {
            //         grid.lpcoAtchFileList.deleteRow("checkbox");
            //     },
            // });
        },
        click_Btn_AddGridchpnList: () => {
            grid.chpnList.addRow();
        },
        click_Btn_DeleteGridChpnList: () => {
            const seltList: any[] = grid.chpnList.getChecked() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }

            grid.chpnList.deleteRow("checkbox");

            // modal.openModal({
            //     content: "msg.osc.00095",
            //     onConfirm: () => {
            //         grid.lpcoAtchFileList.deleteRow("checkbox");
            //     },
            // });
        },
        getAppDtl: (appCdNo: any) => {
            fetch.getAppDtl.fetch(appCdNo);
        },
        getOrgDeptUserList: (orgCd: any, deptCd: any) => {
            fetch.getOrgDeptUserList.fetch(orgCd, deptCd);
        },
        setInit: async () => {
            // 기관 LPCO SELECT 목록
            await fetch.getOrgLpcoByPrcsList.fetch(auth.get("orgCd"));

            // Tree Data
            await fetch.getOrgDeptTreeData.fetch(auth.get("orgCd"));
        },
    };

    // useEffect
    useEffect(() => {
        handler.setInit();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_LPCO_APP_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoInfoRgsr.schema.orgNm}
                                onChange={(ev: any) => {}}
                            ></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoInfoRgsr.schema.appCdNo}
                                options={orgLpcoList}
                                onChange={(ev: any) => {
                                    if (ev) {
                                        handler.getAppDtl(ev);
                                    } else {
                                        grid.chpnList.clearData();
                                        form.lpcoInfoRgsr.setValue("appTpCd", "");
                                    }
                                }}
                            ></Group.Control>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appTpCd}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left size={5} gap={1}>
                    <Group>
                        <Group.Body>
                            <Group.Title title={"T_DEPT"} titleSize={2}></Group.Title>
                            <Tree
                                data={orgDeptTree}
                                onClick={(data: any) => {
                                    const { orgCd, deptCd } = data;
                                    handler.getOrgDeptUserList(orgCd, deptCd);
                                }}
                                _tree={uTree.tree._tree}
                                size={12}
                                height={310}
                                checkbox={false}
                            />
                        </Group.Body>
                    </Group>
                </Layout.Left>
                <Layout.Right size={7} gap={1}>
                    <Group>
                        <Group.Body>
                            <Group.Title title={"T_DEPT_USR_LST"} titleSize={2}></Group.Title>
                            <Layout direction="row">
                                <Layout.Left></Layout.Left>
                                <Layout.Right>
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => {
                                            handler.click_Btn_AddOfficer();
                                        }}
                                    >
                                        {t("B_ADD_OFCR")}
                                    </Button>
                                </Layout.Right>
                            </Layout>
                            {/*
                             * 그리드
                             * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                             * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                             * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                             */}
                            <Grid
                                {...grid.orgDeptUsrList.grid}
                                data={orgDeptUsrList} /* fetch.getRpckItmAppList.data?.orgDeptUsrList */
                                render={render.grid_orgDeptUsrList}
                                // onCellClick={handler.click_Grid_RpckItmAppList}
                                // onRowClick={handler.click_GridRow_RpckItmAppList}
                            />
                        </Group.Body>
                    </Group>
                </Layout.Right>
            </Layout>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_OFCR"} titleSize={2}></Group.Title>
                    <Layout direction="row">
                        <Layout.Left></Layout.Left>
                        <Layout.Right>
                            <Button
                                role="gridDelete"
                                onClick={() => {
                                    handler.click_Btn_DeleteGridChpnList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                    {/*
                     * 그리드
                     * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                     * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                     * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                     */}
                    <Grid
                        {...grid.chpnList.grid}
                        data={chpnList} /* fetch.getRpckItmAppList.data?.orgDeptUsrList */
                        render={render.grid_chpnList}
                    />
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203026q);
                        }}
                    ></Button>
                    <Button
                        role="reset"
                        onClick={() => {
                            form.lpcoInfoRgsr.reset({ ...lpcoUtils.orgInfo });
                            //form.lpcoInfoRgsr.setValues({ ...lpcoUtils.orgInfo });

                            grid.orgDeptUsrList.resetData();
                            grid.chpnList.resetData();
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.saveAppChpn}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
